import { memo, useContext, useEffect, useRef } from "react";
import AdyenCheckout from "@adyen/adyen-web";
import "./AdyenPayment.scss";
import propTypes from "prop-types";
import appGlobalsContext from "app/AppGlobalsContext";

// https://docs.adyen.com/payment-methods/cards/custom-card-integration/#supported-card-types
const AdyenPayment = ({
	acceptedCreditCardTypes = [],
	onChange = () => {},
	onAdyenComponentMounted,
	onError = () => {},
}) => {
	const { shop, envVars } = useContext(appGlobalsContext);
	const paymentContainer = useRef(null);

	const adyenClientKey = envVars?.ADYEN_CLIENT_ID;
	const environment = envVars?.ENVIRONMENT;

	useEffect(() => {
		let ignore = false;

		if (!paymentContainer.current) {
			// initiateCheckout is not finished yet.
			return;
		}

		const createCheckout = async () => {
			const adyenEnvironment = ["live", "preprod", "canary"].some(env =>
				environment?.includes(env)
			)
				? "live"
				: "test";

			const checkout = await AdyenCheckout({
				locale: shop, // The shopper's locale. This is used to set the language rendered in the UI.
				environment: adyenEnvironment, // @see https://docs.adyen.com/online-payments/build-your-integration/advanced-flow/?platform=Web&integration=Components&version=5.60.0#testing-your-integration
				clientKey: adyenClientKey,
				// @see https://github.com/Adyen/adyen-web/tree/main/packages/lib/src/language/locales
				// we want to hide de default placeholder "J. Smith"
				translations: {
					ar: {
						"creditCard.holderName.placeholder": "",
					},
					de: {
						"creditCard.holderName.placeholder": "",
					},
					en: {
						"creditCard.holderName.placeholder": "",
					},
					fr: {
						"creditCard.holderName.placeholder": "",
					},
					es: {
						"creditCard.holderName.placeholder": "",
					},
					it: {
						"creditCard.holderName.placeholder": "",
					},
					nl: {
						"creditCard.holderName.placeholder": "",
					},
				},
				countryCode: shop?.split("_")[1], // The shopper's country code. A valid value is an ISO two-character country code (e.g. 'NL')
				showPayButton: false, // Create an event handler for this event, which is called when the shopper selects the Pay button.
				setStatusAutomatically: true,
				onChange: state => {
					const paymentMethod = state?.data?.paymentMethod;
					// save encryptedCard on redux store or a state to send it to the backend
					if (state.isValid) {
						onChange(JSON.stringify(paymentMethod));
					}
				},
				onError: error => {
					// eslint-disable-next-line no-console
					console.error("onError", error);
					onError(error);
				},
			});

			// The 'ignore' flag is used to avoid double re-rendering caused by React 18 StrictMode
			// More about it here: https://beta.reactjs.org/learn/synchronizing-with-effects#fetching-data
			if (paymentContainer.current && !ignore) {
				const cardComponent = checkout
					.create("card", {
						// https://docs.adyen.com/payment-methods/cards/web-component/#optional-configuration
						type: "card",
						// https://docs.adyen.com/payment-methods/cards/custom-card-integration/#supported-card-types
						brands: acceptedCreditCardTypes,
						showBrandsUnderCardNumber: true,
						enableStoreDetails: false,
						hasHolderName: true,
						showFormInstruction: false,
						holderNameRequired: true,
						positionHolderNameOnTop: false,
						billingAddressRequired: false,
						maskSecurityCode: false, // Set to true to mask the CVV/CVC code when your shopper enters it in the Card Component.
						// @see https://docs.adyen.com/online-payments/build-your-integration/sessions-flow/?platform=Web&integration=Drop-in&version=5.60.0#styling-card-input-fields
						styles: {
							placeholder: {
								fontSize: "14px",
							},
							base: {
								fontSize: "14px",
							},
						},
					})
					.mount(paymentContainer.current);

				// make cardComponent available to parent component to cal submit from a custom button when showPaymentButton is false
				onAdyenComponentMounted(cardComponent);
			}
		};

		createCheckout();

		// eslint-disable-next-line consistent-return
		return () => {
			ignore = true;
		};
	}, [paymentContainer.current, acceptedCreditCardTypes, adyenClientKey, environment, shop]);

	return <div className="adyen-payment" id="component-container" ref={paymentContainer} />;
};

AdyenPayment.propTypes = {
	onChange: propTypes.func,
	onError: propTypes.func,
	onAdyenComponentMounted: propTypes.func,
	acceptedCreditCardTypes: propTypes.array,
};

export default memo(AdyenPayment);
